import { getCookie, setCookieWithExpiry } from 'src/utils/cookies';
import { runScript } from 'src/utils/eval';
import { navigate } from 'src/utils/navigation';
import { mapPaySourceToPaymentType } from 'src/utils/payments';
import { createUrl, getUrlParameters } from 'src/utils/url';
import { isValidUrl } from 'src/utils/validations';

import { Api } from '../../apis/api';
import { IImportOneClickPayload, PaySource } from '../../types/api';
import { OrderFlow } from '../../types/order';
import { getAppUtms } from '../../utils/utms';
import { FunnelController } from '../business/FunnelController';
import { UiController } from '../ui/UiController';

let NON_RESPONSIVE_TS: NodeJS.Timeout | null = null;

export class OneClickController {
  private fc: FunnelController;

  constructor(fc: FunnelController) {
    this.fc = fc;
  }

  private getParams = () => {
    const auid = getAppUtms().custom_order_auid;
    const lead = this.fc.lead.lead;
    const orderId = lead.orderId as string;
    const sessionId = this.fc.config.sessionId;
    const product = this.fc.getSelectedProduct();
    const successRedirect = this.fc.config?.redirectTo;
    const serverRedirectUrl = this.fc.config?.ppSuccessRedirectUrl;

    if (!product) {
      throw new Error('Error: Products is undefined');
    }
    if (!sessionId) {
      throw new Error('Error: sessionId is undefined in OneClick');
    }
    if (!successRedirect) {
      throw new Error('Error: successRedirect is undefined');
    }
    if (!serverRedirectUrl) {
      throw new Error('Error: serverRedirUrl is undefined');
    }

    return {
      auid,
      lead,
      orderId,
      product,
      sessionId,
      successRedirect,
      serverRedirectUrl,
    };
  };

  createOneClickOrder = async () => {
    UiController.setUpsellButtonLoading(true);

    try {
      const {
        auid,
        lead,
        orderId,
        product,
        sessionId,
        successRedirect,
        serverRedirectUrl,
      } = this.getParams();

      if (!successRedirect) {
        throw new Error('Error: successRedirect is undefined');
      }
      if (!serverRedirectUrl) {
        throw new Error('Error: serverRedirectUrl is undefined');
      }
      // User can make only one 1click order per hour
      const disallowedToBuy = getCookie('disallow_click_order');
      if (disallowedToBuy) {
        UiController.setUpsellButtonLoading(false);
        return 'block';
      }

      // TODO: Fix paypal reach 1click flow
      const orderData: IImportOneClickPayload = {
        orderId,
        sessionId,
        sourceValue3: auid,
        client: window.BRAND,
        salesUrl: location.href,
        custom1: getUrlParameters('ad_id'),
        custom2: lead.paymentType || mapPaySourceToPaymentType(lead.paySource),
        custom3: `${window.location.origin + window.location.pathname}`,
        product1_id: product.id.toString(),
        product1_price: product.finalPrice,
        campaignId: this.fc.config.campaignId,
        product1_shipPrice: product.shippingPrice || 0,
        redirectsTo: createUrl(serverRedirectUrl, {
          redirectTo: successRedirect,
        }),
        errorRedirectsTo: createUrl('/products-order/us', {
          cancel_url: '1',
          order_id: orderId,
        }),
        custom_order_sid: sessionId,
        custom_user_agent: navigator.userAgent,
        custom_http_referer: window.location.href,
        custom_order_initial: product.id.toString(),
        custom_order_paymentMethod: lead.paymentMethod || '',
        custom_order_paymentType: lead.paymentType || '',
        custom_order_order_flow: OrderFlow.ONE_CLICK,
        custom_order_prev_order_id: orderId,
        custom_order_mg_tracking_id: getUrlParameters('mg_tracking_id'),
        note: 'initial-1click',
        ...getAppUtms(),
      };

      NON_RESPONSIVE_TS = Errors.maybeReportNonResponsivePaymentButton(
        PaySource.ONE_CLICK,
      );
      const res = await Api.importOneClick(orderData);

      if (window.DEBUG) {
        console.log(res);
      }

      const newOrderId = res.orderId;
      setCookieWithExpiry('order_id', newOrderId, 10);
      if (!window.DEBUG) {
        setCookieWithExpiry('disallow_click_order', 'true');
      }

      if (isValidUrl(res.url)) {
        window.location.href = res.url;
      } else if (res.script) {
        runScript(res.script);
      } else {
        navigate(successRedirect, { orderId: newOrderId, sessionId });
      }
      return 'success';
    } catch (e) {
      Errors.report(
        e,
        {
          geo: window.GEO,
          config: this.fc.config || 'undefined',
          lead: this.fc.lead.lead || 'undefined',
          product: this.fc.getSelectedProduct() || 'undefined',
          productIdx: window['productIdx'].value || 'undefined',
        },
        false,
        false,
      );

      return 'error';
    } finally {
      NON_RESPONSIVE_TS && clearTimeout(NON_RESPONSIVE_TS);
      UiController.setUpsellButtonLoading(false);
    }
  };
}
